import { useState, useEffect } from 'react';
import { apiGet } from './apiClient';

export interface TagInfo {
    description: string;
    color: string;
}

interface ApiTagLabel {
    tag: string;
    description: string;
    color: string;
}

// Module-level cache: survives re-renders and component remounts within the
// same page load. Labels change only on API deploys, so no TTL is needed.
let _cache: Map<string, TagInfo> | null = null;
let _inflight: Promise<Map<string, TagInfo>> | null = null;

async function _fetchTagLabels(): Promise<Map<string, TagInfo>> {
    if (_cache) return _cache;
    if (_inflight) return _inflight;

    _inflight = apiGet<{ labels: ApiTagLabel[] }>('/api/trader-tags/labels')
        .then(data => {
            const map = new Map<string, TagInfo>();
            for (const { tag, description, color } of data.labels ?? []) {
                map.set(tag, { description, color });
            }
            _cache = map;
            _inflight = null;
            return map;
        })
        .catch(() => {
            _inflight = null;
            return new Map<string, TagInfo>();
        });

    return _inflight;
}

export function useTagLabels(): Map<string, TagInfo> {
    // Initialise synchronously from cache if available — avoids a render cycle
    // on components that mount after the first fetch has resolved.
    const [labels, setLabels] = useState<Map<string, TagInfo>>(
        () => _cache ?? new Map()
    );

    useEffect(() => {
        if (_cache) {
            setLabels(_cache);
            return;
        }
        _fetchTagLabels().then(setLabels);
    }, []);

    return labels;
}
